import React, { useEffect, useState } from "react";
import { Table, Switch, Avatar, Spin, Card, Divider, Row, Col, Typography, Tag, Tooltip, Select } from "antd";
import { UserOutlined, DownOutlined, UpOutlined, CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import GetPositionsWithFeaturesApi from '../../api/position-features/GetPositionsWithFeaturesApi';
import BulkUpdatePositionFeaturesApi from '../../api/position-features/BulkUpdatePositionFeaturesApi';
import GetGlobalSettingsApi from '../../api/global-settings/GetGlobalSettingsApi';
import UpdateGlobalSettingsApi from '../../api/global-settings/UpdateGlobalSettingsApi';
import PermissionService from '../../services/PermissionService';
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";

const { Title, Text } = Typography;

const FEATURES = [
  { key: "prediction",     label: "Prediction"       },
  { key: "timeline",       label: "Timeline"          },
  { key: "timesheet",      label: "Timesheet"         },
  { key: "subtargets",     label: "Sub-Targets"       },
  { key: "chatbot",        label: "Chatbot"           },
  { key: "gem",            label: "GEM AI"            },
  { key: "planApproval",   label: "Plan Approval"     },
  { key: "targetApproval", label: "Target Approval"   },
  { key: "zoho",           label: "Zoho CRM"          },
  { key: "odoo",           label: "Odoo CRM"          },
  { key: "phrm",           label: "PeopleHR"          },
  { key: "whatsapp",       label: "WhatsApp"          },
  { key: "dependencyView", label: "Dependency View"   },
  { key: "dashboard",      label: "Dashboard"         },
  { key: "positions",      label: "Positions"         },
  { key: "units",          label: "Units"             },
  { key: "heatmap",        label: "Heatmap"           },
  { key: "uploadTasks",   label: "Upload Tasks"      },
  { key: "reports",       label: "Reports"           },
];

const enabledKey = (key) => `${key}Enabled`;

function countEnabled(record) {
  return FEATURES.filter(f => !!record[enabledKey(f.key)]).length;
}

// ── Big toggle cell ──────────────────────────────────────────────────────────
function FeatureCell({ record, feature, updating, onToggle }) {
  const isOn = !!record[enabledKey(feature.key)];
  const isLoading = !!updating[`${record.positionId}-${feature.key}`];
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      gap: 4, padding: "6px 2px",
    }}>
      <Switch
        checked={isOn}
        loading={isLoading}
        onChange={(checked) => onToggle(record.positionId, feature.key, checked)}
        style={{
          backgroundColor: isOn ? "#22c55e" : "#fca5a5",
          minWidth: 44,
        }}
      />
      <span style={{
        fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
        color: isOn ? "#16a34a" : "#ef4444",
      }}>
        {isOn ? "ON" : "OFF"}
      </span>
    </div>
  );
}

function PositionFeatures() {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [positionPanelOpen, setPositionPanelOpen] = useState(true);

  const [globalSettings, setGlobalSettings] = useState({
    treeView: false, fullTreeView: false, manageHierarchy: false,
    showHome: true, defaultLandingPage: '/my-plans', enhancedSecurity: false,
  });
  const [globalLoading, setGlobalLoading] = useState(true);
  const [globalUpdating, setGlobalUpdating] = useState({});

  useEffect(() => { fetchPositions(); fetchGlobalSettings(); }, []);

  const fetchPositions = async () => {
    setLoading(true);
    try {
      const r = await GetPositionsWithFeaturesApi();
      if (r.success) setPositions(r.data);
      else openNotificationError("bottomRight", "Failed to fetch positions");
    } catch { openNotificationError("bottomRight", "Error fetching positions"); }
    finally { setLoading(false); }
  };

  const fetchGlobalSettings = async () => {
    setGlobalLoading(true);
    try {
      const r = await GetGlobalSettingsApi();
      if (r.success) setGlobalSettings(r.data);
    } catch (e) { console.error(e); }
    finally { setGlobalLoading(false); }
  };

  // ── toggle one feature ───────────────────────────────────────────────────
  const handleFeatureToggle = async (positionId, featureName, enabled) => {
    const updateKey = `${positionId}-${featureName}`;
    setUpdating(prev => ({ ...prev, [updateKey]: true }));
    try {
      const position = positions.find(p => p.positionId === positionId);
      const features = {};
      FEATURES.forEach(f => { features[f.key] = !!position[enabledKey(f.key)]; });
      features[featureName] = enabled;
      const r = await BulkUpdatePositionFeaturesApi(positionId, features);
      if (r.success) {
        setPositions(prev => prev.map(p =>
          p.positionId === positionId ? { ...p, [`${featureName}Enabled`]: enabled } : p
        ));
        PermissionService.clearCache();
        openNotificationSuccess("bottomRight", "Feature updated");
      } else openNotificationError("bottomRight", r.message || "Failed to update");
    } catch { openNotificationError("bottomRight", "Error updating feature"); }
    finally { setUpdating(prev => { const s = { ...prev }; delete s[updateKey]; return s; }); }
  };

  // ── enable / disable ALL for one position ────────────────────────────────
  const handleBulkToggle = async (positionId, enableAll) => {
    const bulkKey = `${positionId}-bulk`;
    setUpdating(prev => ({ ...prev, [bulkKey]: true }));
    try {
      const features = {};
      FEATURES.forEach(f => { features[f.key] = enableAll; });
      const r = await BulkUpdatePositionFeaturesApi(positionId, features);
      if (r.success) {
        setPositions(prev => prev.map(p => {
          if (p.positionId !== positionId) return p;
          const updated = { ...p };
          FEATURES.forEach(f => { updated[`${f.key}Enabled`] = enableAll; });
          return updated;
        }));
        PermissionService.clearCache();
        openNotificationSuccess("bottomRight", enableAll ? "All features enabled" : "All features disabled");
      } else openNotificationError("bottomRight", r.message || "Failed to update");
    } catch { openNotificationError("bottomRight", "Error updating features"); }
    finally { setUpdating(prev => { const s = { ...prev }; delete s[bulkKey]; return s; }); }
  };

  // ── global settings ──────────────────────────────────────────────────────
  const handleGlobalToggle = async (settingKey, enabled) => {
    setGlobalUpdating(prev => ({ ...prev, [settingKey]: true }));
    try {
      const r = await UpdateGlobalSettingsApi({ [settingKey]: enabled });
      if (r.success) {
        setGlobalSettings(r.data);
        window.dispatchEvent(new CustomEvent("globalSettingsUpdated"));
        openNotificationSuccess("bottomRight", "Setting updated");
      } else openNotificationError("bottomRight", r.message || "Failed to update");
    } catch { openNotificationError("bottomRight", "Error updating setting"); }
    finally { setGlobalUpdating(prev => { const s = { ...prev }; delete s[settingKey]; return s; }); }
  };

  const handleLandingPageChange = async (value) => {
    setGlobalUpdating(prev => ({ ...prev, defaultLandingPage: true }));
    try {
      const r = await UpdateGlobalSettingsApi({ defaultLandingPage: value });
      if (r.success) {
        setGlobalSettings(r.data);
        window.dispatchEvent(new CustomEvent("globalSettingsUpdated"));
        openNotificationSuccess("bottomRight", "Default landing page updated");
      } else openNotificationError("bottomRight", r.message || "Failed to update");
    } catch { openNotificationError("bottomRight", "Error updating landing page"); }
    finally { setGlobalUpdating(prev => { const s = { ...prev }; delete s.defaultLandingPage; return s; }); }
  };

  const globalFeatureRows = [
    { key: "treeView",        label: "Tree View",        description: "Show the Tree View button in the top plans header bar" },
    { key: "fullTreeView",    label: "Full Tree View",   description: "Show the Full Tree View button in the top plans header bar" },
    { key: "manageHierarchy", label: "Manage Hierarchy", description: "Show the Manage Hierarchy button on each plan card" },
    { key: "showSynergen",    label: "SynergenHR Button", description: "Show the SynergenHR switcher button in the header navigation" },
    { key: "enhancedSecurity", label: "Enhanced Security (2FA + Lockout)", description: "Enable 2FA login verification, account lockout after 3 failed attempts, 60-day password expiry, and password history (last 5)" },
  ];

  // ── columns ──────────────────────────────────────────────────────────────
  const columns = [
    {
      title: <span style={{ fontWeight: 700, fontSize: 13 }}>Position &amp; User</span>,
      key: "position",
      fixed: "left",
      width: 260,
      render: (_, record) => {
        const enabled = countEnabled(record);
        const total = FEATURES.length;
        const allOn = enabled === total;
        const isBulkLoading = !!updating[`${record.positionId}-bulk`];
        // avatar border: green = all on, amber = partial, red = all off
        const borderColor = allOn ? "#22c55e" : enabled === 0 ? "#ef4444" : "#f59e0b";
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0" }}>
            <div style={{ position: "relative", flexShrink: 0 }}>
              <Avatar
                src={record.imageUrl}
                icon={<UserOutlined />}
                size={46}
                style={{ border: `2.5px solid ${borderColor}` }}
              />
              <span style={{
                position: "absolute", bottom: 0, right: 0,
                width: 11, height: 11, borderRadius: "50%",
                background: borderColor, border: "2px solid #fff",
              }} />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#111", lineHeight: "18px",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {record.designationName}
              </div>
              {record.firstName && (
                <div style={{ fontSize: 12, color: "#555", marginTop: 1,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {record.firstName} {record.lastName}
                  {record.unitName && <span style={{ color: "#aaa" }}> · {record.unitName}</span>}
                </div>
              )}
              <div style={{ marginTop: 5, display: "flex", alignItems: "center", gap: 8 }}>
                <Tag
                  style={{
                    margin: 0, fontSize: 11, fontWeight: 700,
                    padding: "1px 7px", borderRadius: 20,
                    background: allOn ? "#dcfce7" : enabled === 0 ? "#fee2e2" : "#fef9c3",
                    border: `1px solid ${allOn ? "#86efac" : enabled === 0 ? "#fca5a5" : "#fde68a"}`,
                    color: allOn ? "#16a34a" : enabled === 0 ? "#dc2626" : "#92400e",
                  }}
                >
                  {enabled}/{total} on
                </Tag>
                {!isBulkLoading ? (
                  <span
                    onClick={() => handleBulkToggle(record.positionId, !allOn)}
                    style={{ fontSize: 11, color: "#3b82f6", cursor: "pointer",
                      textDecoration: "underline", fontWeight: 500 }}
                  >
                    {allOn ? "Disable All" : "Enable All"}
                  </span>
                ) : (
                  <Spin size="small" />
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    ...FEATURES.map(feature => ({
      title: (
        <div style={{ textAlign: "center", fontSize: 12, fontWeight: 600,
          color: "#374151", lineHeight: "15px", padding: "2px 0" }}>
          {feature.label}
        </div>
      ),
      key: feature.key,
      align: "center",
      width: 90,
      render: (_, record) => (
        <FeatureCell
          record={record}
          feature={feature}
          updating={updating}
          onToggle={handleFeatureToggle}
        />
      ),
      onCell: (record) => ({
        style: {
          background: !!record[enabledKey(feature.key)] ? "transparent" : "rgba(254,202,202,0.18)",
          transition: "background 0.15s",
        },
      }),
    })),
  ];

  // ── row background ────────────────────────────────────────────────────────
  const rowClassName = (record) => {
    const en = countEnabled(record);
    if (en === 0)              return "pf-row-all-off";
    if (en === FEATURES.length) return "pf-row-all-on";
    return "pf-row-partial";
  };

  return (
    <div className="p-6">

      {/* ── Position Features (collapsible) ─────────────────────────── */}
      <Card className="mb-6" bodyStyle={{ padding: 0 }} style={{ borderRadius: 10, overflow: "hidden" }}>

        {/* Collapsible header */}
        <div
          onClick={() => setPositionPanelOpen(v => !v)}
          style={{
            padding: "16px 24px", cursor: "pointer", userSelect: "none",
            borderBottom: positionPanelOpen ? "1px solid #f0f0f0" : "none",
            background: "#fafafa", display: "flex", alignItems: "center", justifyContent: "space-between",
          }}
        >
          <div>
            <Title level={4} style={{ margin: 0 }}>Position Features</Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Enable or disable features per position. Use Enable All / Disable All for quick bulk changes.
            </Text>
          </div>
          <span style={{ color: "#aaa", fontSize: 16 }}>
            {positionPanelOpen ? <UpOutlined /> : <DownOutlined />}
          </span>
        </div>

        {positionPanelOpen && (
          <>
            <style>{`
              .pf-row-all-on  > td { background: #fff !important; }
              .pf-row-partial > td.ant-table-cell-fix-left { background: #fffdf5 !important; }
              .pf-row-all-off > td { background: #fff8f8 !important; }
              .pf-row-all-off > td.ant-table-cell-fix-left { background: #fff0f0 !important; }

              /* bigger toggle thumb */
              .pf-table .ant-switch { min-width: 44px; height: 24px; }
              .pf-table .ant-switch .ant-switch-handle { width: 20px; height: 20px; top: 2px; }
              .pf-table .ant-switch-checked .ant-switch-handle { left: calc(100% - 22px); }

              /* green when on, salmon when off */
              .pf-table .ant-switch-checked { background-color: #22c55e !important; }
              .pf-table .ant-switch:not(.ant-switch-checked) { background-color: #fca5a5 !important; }

              /* header cells */
              .pf-table .ant-table-thead > tr > th {
                background: #f8fafc !important;
                border-bottom: 2px solid #e5e7eb !important;
                padding: 10px 6px !important;
              }
              .pf-table .ant-table-tbody > tr > td {
                padding: 0 6px !important;
                border-bottom: 1px solid #f3f4f6 !important;
              }
            `}</style>

            <Table
              className="pf-table"
              columns={columns}
              dataSource={positions}
              rowKey="positionId"
              loading={loading}
              rowClassName={rowClassName}
              size="small"
              scroll={{ y: 520, x: "max-content" }}
              pagination={{
                pageSize: 15,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} positions`,
                pageSizeOptions: ['10', '15', '25', '50'],
              }}
            />
          </>
        )}
      </Card>

      {/* ── Global Feature Settings ───────────────────────────────────── */}
      <Card style={{ borderRadius: 10, marginBottom: 24 }}>
        <Title level={4} style={{ margin: "0 0 4px" }}>Global Feature Settings</Title>
        <Text type="secondary" style={{ fontSize: 13 }}>
          System-wide UI toggles that apply to all users, regardless of position.
        </Text>
        <Divider style={{ margin: "14px 0" }} />
        {globalLoading ? <Spin /> : (
          <div>
            <Text style={{ display: "block", marginBottom: 10, fontSize: 11, fontWeight: 700,
              color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Top Plans — Hierarchy Buttons
            </Text>
            {globalFeatureRows.map(row => {
              const isOn = !!globalSettings[row.key];
              return (
                <Row key={row.key} align="middle" style={{
                  padding: "11px 16px", borderRadius: 8, marginBottom: 8,
                  background: isOn ? "#f0fdf4" : "#fff5f5",
                  border: `1px solid ${isOn ? "#86efac" : "#fecaca"}`,
                  transition: "all 0.2s",
                }}>
                  <Col flex="auto">
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {isOn
                        ? <CheckCircleFilled style={{ color: "#22c55e", fontSize: 15 }} />
                        : <CloseCircleFilled style={{ color: "#ef4444", fontSize: 15 }} />
                      }
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{row.label}</span>
                      <Tag color={isOn ? "success" : "error"} style={{ fontSize: 11, margin: 0 }}>
                        {isOn ? "Enabled" : "Disabled"}
                      </Tag>
                    </div>
                    <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2, paddingLeft: 23 }}>
                      {row.description}
                    </div>
                  </Col>
                  <Col>
                    <Switch
                      checked={isOn}
                      loading={!!globalUpdating[row.key]}
                      onChange={checked => handleGlobalToggle(row.key, checked)}
                      style={{ backgroundColor: isOn ? "#22c55e" : "#fca5a5" }}
                    />
                  </Col>
                </Row>
              );
            })}
          </div>
        )}
      </Card>

      {/* ── Navigation Settings ───────────────────────────────────────── */}
      <Card style={{ borderRadius: 10 }}>
        <Title level={4} style={{ margin: "0 0 4px" }}>Navigation Settings</Title>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Control which pages are visible and where users land after logging in.
        </Text>
        <Divider style={{ margin: "14px 0" }} />
        {globalLoading ? <Spin /> : (
          <div>
            <Text style={{ display: "block", marginBottom: 10, fontSize: 11, fontWeight: 700,
              color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Page Visibility
            </Text>
            {(() => {
              const isOn = globalSettings.showHome !== false;
              return (
                <Row align="middle" style={{
                  padding: "11px 16px", borderRadius: 8, marginBottom: 16,
                  background: isOn ? "#f0fdf4" : "#fff5f5",
                  border: `1px solid ${isOn ? "#86efac" : "#fecaca"}`,
                  transition: "all 0.2s",
                }}>
                  <Col flex="auto">
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {isOn
                        ? <CheckCircleFilled style={{ color: "#22c55e", fontSize: 15 }} />
                        : <CloseCircleFilled style={{ color: "#ef4444", fontSize: 15 }} />
                      }
                      <span style={{ fontWeight: 600, fontSize: 14 }}>Home Page</span>
                      <Tag color={isOn ? "success" : "error"} style={{ fontSize: 11, margin: 0 }}>
                        {isOn ? "Visible" : "Hidden"}
                      </Tag>
                    </div>
                    <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2, paddingLeft: 23 }}>
                      Show or hide the Home page in the sidebar for all users
                    </div>
                  </Col>
                  <Col>
                    <Switch
                      checked={isOn}
                      loading={!!globalUpdating.showHome}
                      onChange={checked => handleGlobalToggle("showHome", checked)}
                      style={{ backgroundColor: isOn ? "#22c55e" : "#fca5a5" }}
                    />
                  </Col>
                </Row>
              );
            })()}

            <Divider style={{ margin: "12px 0" }} />
            <Text style={{ display: "block", marginBottom: 10, fontSize: 11, fontWeight: 700,
              color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Default Landing Page
            </Text>
            <Row align="middle" style={{
              padding: "12px 16px", borderRadius: 8,
              background: "#f8fafc", border: "1px solid #e5e7eb",
            }}>
              <Col flex="auto">
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>Landing page after login</div>
                <div style={{ fontSize: 12, color: "#9ca3af" }}>
                  Where users are sent after logging in or navigating to an unknown route
                </div>
              </Col>
              <Col>
                <Select
                  value={globalSettings.defaultLandingPage || "/my-plans"}
                  onChange={handleLandingPageChange}
                  loading={!!globalUpdating.defaultLandingPage}
                  style={{ width: 200 }}
                  options={[
                    { value: "/home",                  label: "Home" },
                    { value: "/my-plans",              label: "My Plans" },
                    { value: "/top-plans",             label: "Top Plans" },
                    { value: "/reports",               label: "Reports" },
                    { value: "/timesheets",            label: "Timesheets" },
                    { value: "/timeline",              label: "Timeline" },
                    { value: "/positions-performance", label: "Positions" },
                    { value: "/units",                 label: "Units" },
                    { value: "/metabase",              label: "Dashboard" },
                  ]}
                />
              </Col>
            </Row>
          </div>
        )}
      </Card>

    </div>
  );
}

export default PositionFeatures;
